<template>
	<div class="flex flex-col items-start">
		<AppAddressField
			v-if="!manualAddress"
			v-model="modeledAddress"
			class="w-full"
			:open-on-focus="true"
		/>

		<div v-else-if="rawAddress" class="flex flex-col gap-2">
			<div>
				<label for="straatnaam-input" class="text-sm"> Straatnaam </label>
				<InputText
					id="straatnaam-input"
					v-model="rawAddress.openbareRuimteNaam"
					class="w-64 text-sm"
				/>
			</div>

			<div class="flex gap-1">
				<div>
					<label for="huisnummer-input" class="text-sm"> Huisnummer </label>
					<InputText
						id="huisnummer-input"
						v-model="rawAddress.huisnummer"
						class="w-24 text-sm"
					/>
				</div>
				<div>
					<label for="huisnummer-input" class="text-sm"> Toevoeging </label>
					<InputText
						id="huisnummer-input"
						v-model="rawAddress.huisnummertoevoeging"
						class="w-24 text-sm"
					/>
				</div>
			</div>

			<div class="flex gap-1">
				<div>
					<label for="postcode-input" class="text-sm"> Postcode </label>
					<InputText
						id="postcode-input"
						v-model="rawAddress.postcode"
						class="w-24 text-sm"
					/>
				</div>

				<div>
					<label for="woonplaatsNaam-input" class="text-sm"> Plaats </label>
					<InputText
						id="woonplaatsNaam-input"
						v-model="rawAddress.woonplaatsNaam"
						class="w-64 text-sm"
					/>
				</div>
			</div>
		</div>

		<AppButton v-if="canSave" variant="link" @click="saveRawAddress">
			Opslaan
		</AppButton>
		<AppButton
			v-else-if="!manualAddress"
			variant="link"
			@click="manualAddress = true"
		>
			handmatig invullen
		</AppButton>
		<AppButton v-else variant="link" @click="manualAddress = false">
			zoek een adres
		</AppButton>
	</div>
</template>

<script setup lang="ts">
import type { AddressResponse } from "@loanlink-nl/model-app";
import { clone, isEqual } from "es-toolkit";
import { computed, nextTick, ref, watch } from "vue";

const modeledAddress = defineModel<AddressResponse | undefined>();
const rawAddress = ref<AddressResponse | undefined>(
	modeledAddress.value ? clone(modeledAddress.value) : undefined,
);

const manualAddress = ref(modeledAddress.value?.manualAddress ?? false);

const canSave = computed(() => {
	return Boolean(
		manualAddress.value &&
		(!modeledAddress.value ||
			!isEqual(
				modeledAddress.value.manualAddress
					? modeledAddress.value
					: modeledAddressToRawAddress(modeledAddress.value),
				rawAddress.value,
			)),
	);
});

function modeledAddressToRawAddress(modeledAddress: AddressResponse) {
	let toevoeging = "";
	if (modeledAddress.huisletter) {
		toevoeging += modeledAddress.huisletter;
	}
	if (modeledAddress.huisnummertoevoeging) {
		toevoeging += modeledAddress.huisnummertoevoeging;
	}

	return {
		openbareRuimteNaam: modeledAddress.openbareRuimteNaam,
		huisnummer: modeledAddress.huisnummer,
		huisnummertoevoeging: toevoeging,
		postcode: modeledAddress.postcode,
		woonplaatsNaam: modeledAddress.woonplaatsNaam,
		manualAddress: true,
	};
}

watch(manualAddress, (manualAddress) => {
	if (manualAddress && modeledAddress.value) {
		rawAddress.value = modeledAddressToRawAddress(modeledAddress.value);
		return;
	}

	if (manualAddress) {
		rawAddress.value = {};
		return;
	}

	if (!manualAddress) {
		rawAddress.value = undefined;
		if (modeledAddress.value) {
			if (!modeledAddress.value.huisnummertoevoeging) {
				modeledAddress.value.huisnummertoevoeging = undefined;
			}
			modeledAddress.value.manualAddress = false;
		}

		void nextTick(() => {
			document.getElementById("address-input")?.focus();
		});
	}
});

function saveRawAddress() {
	modeledAddress.value = rawAddress.value;
}
</script>
